import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TextField,
  Typography,
} from "@mui/material";
import { sv } from "date-fns/locale";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledCell,
  StyledRow,
} from "../../styled-components/styledTableComponents";
import { CHANGE_CARD } from "../../redux/reducers/cardReducer";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";

export function ChangeCardModal() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const currentCard = useSelector(
    (state: any) => state.card.cardWithHistory.current
  );
  const extendedCard = useSelector((state: any) => state.card.extendedCard);
  const personId = useSelector((state: any) => state.card.params.personId);
  const selectedCarePrincipalId = useSelector(
    (state: any) => state.session.selectedCarePrincipal.id
  );

  const [selectedCard, setSelectedCard] = useState(extendedCard ? extendedCard : currentCard);
  const [dateFrom, setDateFrom] = useState(new Date(selectedCard.startDate.toLocaleDateString("sv")));
  const [dateTo, setDateTo] = useState(new Date(selectedCard.endDate.toLocaleDateString("sv")));
  const [preliminaryDate, setPreliminaryDate] = useState(new Date(selectedCard.preliminaryCardDate.toLocaleDateString("sv")));

  const isHistory = location.pathname.includes("history");

  useEffect(() => {
    if (extendedCard && isHistory) {
      setSelectedCard(extendedCard);
      setDateFrom(extendedCard.startDate.toLocaleDateString("sv"));
      setDateTo(extendedCard.endDate.toLocaleDateString("sv"));
      setPreliminaryDate(extendedCard.preliminaryCardDate.toLocaleDateString("sv"))
    } else {
      setSelectedCard(currentCard);
      setDateFrom(currentCard.startDate.toLocaleDateString("sv"));
      setDateTo(currentCard.endDate.toLocaleDateString("sv"));
      setPreliminaryDate(currentCard.preliminaryCardDate.toLocaleDateString("sv"))
    }
  }, []); //eslint-disable-line react-hooks/exhaustive-deps


  const onSubmit = (values: any) => {
    const changeCard = {
      carePrincipalId: selectedCarePrincipalId,
      cardSystemId: selectedCard.cardSystemId,
      cardId: values.cardId,
      cardTypeId: selectedCard.cardType.id,
      startDate: new Date(dateFrom),
      endDate: new Date(dateTo),
      preliminaryCardDate: new Date(preliminaryDate),
    };
    dispatch({ type: CHANGE_CARD, changeCard, personId });
  };

  const setDate = (value: any) => {
    setDateTo(new Date(value.toLocaleDateString("sv")));

    let date = new Date(value.toLocaleDateString("sv"));
    date.setFullYear(date.getFullYear() - 1);
    date.setDate(date.getDate() + 1);
    setDateFrom(date);

    if (preliminaryDate < date) {
      setPreliminaryDate(date);
    }
  };

  const setPrelDate = (value: any) => {
    setPreliminaryDate(new Date(value.toLocaleDateString("sv")));
  }

  return (
    <>
      <Box component="form">
        <Dialog
          open={true}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle id="alert-dialog-title">
              {t("searchCard.menu.changeCard")}
            </DialogTitle>
            <DialogContent>
              <Stack>
                <Table size="small" className="firstColumnBold">
                  <TableBody>
                    <StyledRow>
                      <StyledCell>
                        {t("searchCard.cardInfo.personId")}
                      </StyledCell>
                      {selectedCard.persons.map((person: any) => (
                        <StyledCell key={person.id}>{person.id}</StyledCell>
                      ))}
                    </StyledRow>
                    <StyledRow>
                      <StyledCell>
                        {t("searchCard.cardInfo.cardType")}
                      </StyledCell>
                      <StyledCell>{t(selectedCard.cardType.name)}</StyledCell>
                    </StyledRow>
                    <StyledRow>
                      <StyledCell>{t("searchCard.cardInfo.period")}</StyledCell>
                      <StyledCell>
                        <Stack
                          sx={{ margin: "1vh 0 1vh 0" }}
                          spacing={3}
                          direction="row"
                        >
                          <Controller
                            name="dateFrom"
                            control={control}
                            render={({
                              field: { onChange, value = dateFrom },
                            }) => (
                              <LocalizationProvider
                                adapterLocale={sv}
                                dateAdapter={AdapterDateFns}
                              >
                                <DatePicker
                                  disabled
                                  label={t("dateFrom")}
                                  mask=""
                                  value={value}
                                  onChange={onChange}
                                  renderInput={(params: any) => (
                                    <TextField
                                      {...params}
                                      {...register("dateFrom")}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            )}
                          />
                          <Controller
                            name="dateTo"
                            control={control}
                            render={({
                              field: { onChange, value = dateTo },
                            }) => (
                              <LocalizationProvider
                                adapterLocale={sv}
                                dateAdapter={AdapterDateFns}
                              >
                                <DatePicker
                                  label={t("dateTo")}
                                  mask=""
                                  value={value}
                                  onChange={onChange}
                                  renderInput={(params: any) => (
                                    <TextField
                                      {...params}
                                      {...register("dateTo", {
                                        onChange: (e) =>
                                          setDate(e.target.value),
                                      })}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            )}
                          />
                        </Stack>
                      </StyledCell>
                    </StyledRow>
                    <StyledRow>
                      <StyledCell>
                        {t("searchCard.registerCard.cardFrom")}
                      </StyledCell>
                      <StyledCell>
                        <Controller
                          name="preliminaryCardDate"
                          control={control}
                          render={({
                            field: { onChange, value = preliminaryDate },
                          }) => (
                            <LocalizationProvider
                              adapterLocale={sv}
                              dateAdapter={AdapterDateFns}
                            >
                              <DatePicker
                                label={t("preliminaryCardDate")}
                                mask=""
                                value={value}
                                onChange={onChange}
                                renderInput={(params: any) => (
                                  <TextField
                                    sx={{ margin: "1vh 0 1vh 0" }}
                                    {...params}
                                    {...register("preliminaryCardDate", {onChange: (e) => setPrelDate(e.target.value)})}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          )}
                        />
                      </StyledCell>
                    </StyledRow>
                    <StyledRow>
                      <StyledCell>{t("transactions")}</StyledCell>
                      <StyledCell>{t(selectedCard.currentAmount)}</StyledCell>
                    </StyledRow>
                    <StyledRow>
                      <StyledCell>{t("searchCard.cardInfo.cardId")}</StyledCell>
                      <StyledCell>
                        <Controller
                          name="cardId"
                          control={control}
                          render={({
                            field: { value = selectedCard.cardId },
                          }) => (
                            <TextField
                              value={value}
                              error={errors.amount ? true : false}
                              id="cardId"
                              variant="standard"
                              {...register("cardId", {
                                required: true,
                              })}
                            />
                          )}
                        />
                        {errors.cardId && errors.cardId.type === "required" && (
                          <Typography
                            variant="body2"
                            sx={{ color: "#DC143C" }}
                            role="alert"
                          >
                            {t("errors.emptyField")}
                          </Typography>
                        )}
                      </StyledCell>
                    </StyledRow>
                  </TableBody>
                </Table>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => dispatch({ type: "MODAL_CANCEL" })}
              >
                {t("cancel")}
              </Button>
              <Button variant="contained" color="success" type="submit">
                {t("register")}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </>
  );
}
